import { ClockCircleOutlined, CalendarOutlined } from '@ant-design/icons';
import { Button, Card, Divider, Flex, List, theme, Typography } from 'antd';
import Link from 'antd/es/typography/Link';
import dayjs from 'dayjs';
import { useQuery } from 'jsonapi-react';
import { ArrowRight } from 'lucide-react';
import { useEffect, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthorizedComponent from '../../components/authorized-component';
import { useCartContext } from '../../contexts/cart.context';
import { type CustomerSetting } from '../../entities';
import { useNotification } from '../../hooks/use-notification';
import { Roles } from '../../types';
import { randomUUID } from '../../utils/utils';

const CartCard = (): ReactElement => {
  const { token } = theme.useToken();
  const cartContext = useCartContext();
  const navigate = useNavigate();

  return (
    <Card
      style={{ width: 700, paddingBlock: token.paddingXS }}
      cover={
        <Typography.Title level={3} style={{ paddingInline: token.paddingMD }}>
          Your unplaced order is still waiting for you!
        </Typography.Title>
      }
    >
      <Card.Meta
        title={
          <Typography.Text style={{ fontWeight: 'normal' }}>
            You've drafted an order on{' '}
            <b>{cartContext.customerOrder!.orderDate.format('MMM DD, YYYY')}</b> for:
            <br />
            <ul>
              {[
                ...new Set(
                  cartContext.customerOrder?.siteMenuOrders?.map(
                    (order) => `${order.site.custSiteNumber} - ${order.site.name}`
                  )
                ),
              ].map((siteName) => (
                <li key={randomUUID()}>{siteName}</li>
              ))}
            </ul>
          </Typography.Text>
        }
        description={
          <Flex justify="flex-end" align="center" gap="small">
            <Typography.Title level={5} style={{ paddingInline: token.paddingXXS, marginBlock: 0 }}>
              Go Complete Your Order
            </Typography.Title>
            <Button
              size="large"
              type="primary"
              shape="circle"
              icon={<ArrowRight />}
              onClick={() => {
                navigate('/new-order');
              }}
            />
          </Flex>
        }
      />
    </Card>
  );
};
const OrderDeadLineCard = (props: { customerSettings?: CustomerSetting }): ReactElement => {
  const { token } = theme.useToken();
  const { customerSettings } = props;
  return (
    <Card
      cover={
        <Typography.Title level={3} style={{ paddingInline: token.paddingMD }}>
          Order Deadlines
        </Typography.Title>
      }
      style={{ width: 700, paddingBlock: token.paddingXS }}
    >
      <List
        itemLayout="horizontal"
        dataSource={[
          {
            icon: <ClockCircleOutlined />,
            text: `Due ${customerSettings?.dueDaysToPlaceOrders} days prior to service day by ${dayjs(
              customerSettings?.dueTimeToPlaceOrders,
              'HH:mm'
            ).format('h:mm A')}`,
          },
          {
            icon: <ClockCircleOutlined />,
            text: `Revisions/Cancellation due ${customerSettings?.dueDaysToCancelOrders} day prior to service day by ${dayjs(
              customerSettings?.dueTimeToCancelOrders,
              'HH:mm'
            ).format('h:mm A')}`,
          },
          {
            icon: <CalendarOutlined />,
            text: 'Excluding weekends and holidays',
          },
        ]}
        renderItem={(item) => (
          <List.Item>
            <List.Item.Meta
              avatar={item.icon}
              description={<Typography.Text>{item.text}</Typography.Text>}
            />
          </List.Item>
        )}
      />

      <Divider />

      <div>
        <Typography.Title level={5}>Special Event Orders</Typography.Title>
        <Typography.Text>
          Per emailed request are due <strong>1 week in advance</strong>, with a minimum of{' '}
          <strong>50 guests</strong>.
        </Typography.Text>
      </div>
      <Divider />
      <div>
        <Typography.Text> For more information, please contact us at </Typography.Text>
        <Link underline href="mailto:office@hhcmeals.com">
          office@hhcmeals.com
        </Link>{' '}
        or{' '}
        <Link underline href="mailto:jim.smigo@hhcmeals.com">
          jim.smigo@hhcmeals.com
        </Link>
        <br />
        <Typography.Text> Thank you! </Typography.Text>
      </div>
    </Card>
  );
};

export const DashboardPage = (): ReactElement => {
  const cartContext = useCartContext();

  const customerSetting = useQuery<CustomerSetting[]>('customer-setting');
  const notification = useNotification();
  useEffect(() => {
    customerSetting.error &&
      notification.error({
        message: ' ',
        description: customerSetting?.error?.message ?? 'Failed to load Order Deadlines',
      });
  }, [customerSetting.error]);

  return (
    <Flex gap="middle" vertical>
      <AuthorizedComponent roles={[Roles.ROLE_CUSTOMER_STANDARD, Roles.ROLE_CUSTOMER_ADMIN]}>
        {customerSetting.data?.[0] && (
          <Flex gap="middle" align="flex-start">
            <OrderDeadLineCard customerSettings={customerSetting.data?.[0]} />
          </Flex>
        )}
      </AuthorizedComponent>
      <Flex gap="middle" align="flex-start">
        {cartContext.itemsCount > 0 && <CartCard />}
      </Flex>
    </Flex>
  );
};
